import { db } from '../firebase';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp, collection, getDocs, query, orderBy } from 'firebase/firestore';

/**
 * Get or create a user profile
 * @param {object} user - Firebase auth user object
 * @returns {object} User profile data
 */
export async function getOrCreateUserProfile(user) {
  try {
    const userRef = doc(db, 'users', user.uid);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      // User exists, return profile
      return { id: userSnap.id, ...userSnap.data() };
    } else {
      // Create new user profile
      const newProfile = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || '',
        photoURL: user.photoURL || '',
        role: 'user', // Default role
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        settings: {
          emailNotifications: true,
          theme: 'dark' // PatternLock uses dark theme
        }
      };
      
      await setDoc(userRef, newProfile);
      console.log('✅ New user profile created:', user.uid);
      
      return { id: user.uid, ...newProfile };
    }
  } catch (error) {
    console.error('❌ Error getting/creating user profile:', error);
    throw error;
  }
}

/**
 * Update user profile
 * @param {string} userId - User ID
 * @param {object} updates - Profile updates
 * @returns {boolean} Success status
 */
export async function updateUserProfile(userId, updates) {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
    console.log('✅ User profile updated:', userId);
    return true;
  } catch (error) {
    console.error('❌ Error updating user profile:', error);
    return false;
  }
}

/**
 * Get user profile by ID
 * @param {string} userId - User ID
 * @returns {object|null} User profile or null
 */
export async function getUserProfile(userId) {
  try {
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      return { id: userSnap.id, ...userSnap.data() };
    }
    return null;
  } catch (error) {
    console.error('❌ Error fetching user profile:', error);
    return null;
  }
}

/**
 * Get all users (admin function)
 * @returns {array} All users
 */
export async function getAllUsers() {
  try {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    
    const users = [];
    querySnapshot.forEach((doc) => {
      users.push({ id: doc.id, ...doc.data() });
    });
    
    return users;
  } catch (error) {
    console.error('❌ Error fetching all users:', error);
    return [];
  }
}

/**
 * Update user role (admin function)
 * @param {string} userId - User ID
 * @param {string} role - New role (user, admin)
 * @returns {boolean} Success status
 */
export async function updateUserRole(userId, role) {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      role,
      updatedAt: serverTimestamp()
    });
    console.log('✅ User role updated:', userId, role);
    return true;
  } catch (error) {
    console.error('❌ Error updating user role:', error);
    return false;
  }
}

